/**
 * ErrorBoundary - React エラーバウンダリー
 * コンポーネントエラーの捕捉、ログ記録、復旧機能を提供
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import './ErrorBoundary.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
}

interface ErrorBoundaryProps {
  /** 子コンポーネント */
  children: ReactNode;
  /** フォールバックUI */
  fallback?: (error: Error, retry: () => void) => ReactNode;
  /** エラー発生時のコールバック */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** 最大リトライ回数 */
  maxRetries?: number;
  /** カスタムクラス名 */
  className?: string;
  /** エラー境界の名前（ログ用） */
  boundaryName?: string;
  /** 開発環境でエラー詳細を表示するか */
  showErrorDetails?: boolean;
  /** 自動リトライを有効にするか */
  enableAutoRetry?: boolean;
  /** 自動リトライ間隔（ms） */
  autoRetryDelay?: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, boundaryName = 'Unknown' } = this.props;
    
    // エラー情報を状態に保存
    this.setState({
      error,
      errorInfo
    });

    // エラーログ記録
    this.logError(error, errorInfo, boundaryName);

    // カスタムエラーハンドラーを実行
    onError?.(error, errorInfo);

    // 自動リトライが有効な場合
    if (this.props.enableAutoRetry && this.state.retryCount < (this.props.maxRetries || 3)) {
      this.scheduleAutoRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private logError = (error: Error, errorInfo: ErrorInfo, boundaryName: string) => {
    const errorData = {
      boundary: boundaryName,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount
    };

    // コンソールにエラー出力
    console.group(`🚨 Error Boundary: ${boundaryName}`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Full Error Data:', errorData);
    console.groupEnd();

    // 外部ログ記録サービスに送信（実装は環境による）
    if (process.env.NODE_ENV === 'production') {
      this.sendErrorToService(errorData);
    }
  };

  private sendErrorToService = async (errorData: any) => {
    try {
      // ここで外部のエラー記録サービスにデータを送信
      // 例: Sentry, LogRocket, カスタムAPIなど
      console.log('Sending error to logging service:', errorData);
    } catch (loggingError) {
      console.error('Failed to send error to logging service:', loggingError);
    }
  };

  private scheduleAutoRetry = () => {
    const delay = this.props.autoRetryDelay || 3000;
    
    this.retryTimeout = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    
    if (this.state.retryCount >= maxRetries) {
      console.warn(`Max retries (${maxRetries}) reached for error boundary`);
      return;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: true
    }));

    // リトライ状態をリセット
    setTimeout(() => {
      this.setState({ isRetrying: false });
    }, 500);
  };

  private handleManualRetry = () => {
    this.handleRetry();
  };

  private handleReset = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private renderDefaultErrorUI = () => {
    const { error, errorInfo, retryCount, isRetrying } = this.state;
    const { maxRetries = 3, showErrorDetails = process.env.NODE_ENV === 'development' } = this.props;
    const canRetry = retryCount < maxRetries;

    return (
      <div className="error-boundary">
        <div className="error-content">
          <div className="error-icon">
            <AlertTriangle size={48} />
          </div>
          
          <h2 className="error-title">
            Something went wrong
          </h2>
          
          <p className="error-description">
            We encountered an unexpected error. This has been automatically reported to our team.
          </p>

          {showErrorDetails && error && (
            <details className="error-details">
              <summary>Error Details (Development)</summary>
              <div className="error-info">
                <div className="error-section">
                  <h4>Error Message:</h4>
                  <pre>{error.message}</pre>
                </div>
                
                {error.stack && (
                  <div className="error-section">
                    <h4>Stack Trace:</h4>
                    <pre>{error.stack}</pre>
                  </div>
                )}
                
                {errorInfo?.componentStack && (
                  <div className="error-section">
                    <h4>Component Stack:</h4>
                    <pre>{errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="error-actions">
            {canRetry && (
              <button
                className="retry-btn primary"
                onClick={this.handleManualRetry}
                disabled={isRetrying}
              >
                <RefreshCw size={16} className={isRetrying ? 'spinning' : ''} />
                {isRetrying ? 'Retrying...' : `Retry (${retryCount}/${maxRetries})`}
              </button>
            )}
            
            <button
              className="action-btn secondary"
              onClick={this.handleReset}
            >
              <Bug size={16} />
              Reset Component
            </button>
            
            <button
              className="action-btn secondary"
              onClick={this.handleReload}
            >
              <RefreshCw size={16} />
              Reload Page
            </button>
            
            <button
              className="action-btn secondary"
              onClick={this.handleGoHome}
            >
              <Home size={16} />
              Go Home
            </button>
          </div>

          {retryCount > 0 && (
            <div className="retry-info">
              <span>Retry attempts: {retryCount}/{maxRetries}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { hasError, error, isRetrying } = this.state;
    const { children, fallback, className = '' } = this.props;

    if (hasError && error) {
      // カスタムフォールバックUIがある場合
      if (fallback) {
        return (
          <div className={`error-boundary-wrapper ${className}`}>
            {fallback(error, this.handleManualRetry)}
          </div>
        );
      }

      // デフォルトエラーUI
      return (
        <div className={`error-boundary-wrapper ${className}`}>
          {this.renderDefaultErrorUI()}
        </div>
      );
    }

    // リトライ中の場合はローディング表示
    if (isRetrying) {
      return (
        <div className={`error-boundary-wrapper ${className} retrying`}>
          <div className="retry-overlay">
            <RefreshCw size={24} className="spinning" />
            <span>Retrying...</span>
          </div>
          {children}
        </div>
      );
    }

    return children;
  }
}

// HOC形式での利用用
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;